import anyLeague from "../../classificators/league/anyLeague.js";
import battingScopeContext from "../../classificators/resultsSlice/battingScopeContext.js";
import pitchingScopeContext from "../../classificators/resultsSlice/pitchingScopeContext.js";
import resultsSliceForPitchType from "../../classificators/resultsSlice/resultsSliceForPitchType.js";
import satisfiesAll from "../../classificators/satisfiesAll.js";
import sessionWithPitchOrHitData from "../../classificators/session/sessionWithPitchOrHitData.js";
import IterableDataProcessor from "../../entities/IterableDataProcessor.js";
import RankingStat from "../../entities/RankingStat.js";
import BattedBallType from "../../enums/BattedBallType.js";
import BattingRankingStats from "../../enums/BattingRankingStats.js";
import DataSection from "../../enums/DataSection.js";
import HitReprocessed from "../../enums/HitReprocessed.js";
import PitchingRankingStats from "../../enums/PitchingRankingStats.js";
import PitchReprocessed from "../../enums/PitchReprocessed.js";
import RankingScopeContext from "../../enums/RankingScopeContext.js";
import ResultData from "../../enums/ResultData.js";
import ResultType from "../../enums/ResultType.js";
import TaggingData from "../../enums/TaggingData.js";
import ClassifiedAsBattedBallEventFilter from "../../filters/contextual/ClassifiedAsBattedBallEventFilter.js";
import ContextualFilterBase from "../../filters/contextual/ContextualFilterBase.js";
import NumberFormatter from "../../formatters/NumberFormatter.js";
import EnumValueProvider from "../../providers/EnumValueProvider.js";
import RankingStatCaseVerifier from "../../validators/RankingStatCaseVerifier.js";
import ResultsSlice from "../../valueObjects/ResultsSlice.js";
import MultiRankingStatCalculator from "./MultiRankingStatCalculator.js";

class BattedBallTypeRateCalculator extends MultiRankingStatCalculator {
    constructor(context, customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache, contextualResultFilter) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(contextualResultFilter) !== 'undefined' && !(contextualResultFilter instanceof ContextualFilterBase)) {
            throw new TypeError('contextualResultFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        super(
            context === RankingScopeContext.Pitching.key
                ? [ PitchingRankingStats.LineDrives, PitchingRankingStats.FlyBalls,
                    PitchingRankingStats.PopUps, PitchingRankingStats.GroundBalls,
                    PitchingRankingStats.LineDriveRate, PitchingRankingStats.FlyBallRate,
                    PitchingRankingStats.PopUpRate, PitchingRankingStats.GroundBallRate, ]
                : [ BattingRankingStats.LineDrives, BattingRankingStats.FlyBalls,
                    BattingRankingStats.PopUps, BattingRankingStats.GroundBalls,
                    BattingRankingStats.LineDriveRate, BattingRankingStats.FlyBallRate,
                    BattingRankingStats.PopUpRate, BattingRankingStats.GroundBallRate, ]
            ,
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithPitchOrHitData,
                satisfiesAll(
                    context === RankingScopeContext.Pitching.key 
                        ? pitchingScopeContext
                        : battingScopeContext
                    ,
                    resultsSliceForPitchType
                )
            ),
            skipCache
        );

        this.usageCache = [];
        this.skipCache = !!skipCache;
        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.contextualFilter = contextualResultFilter || new ClassifiedAsBattedBallEventFilter(this.resultsProcessor);
        this.context = context;
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        const bbEvents = this.filterResults(slice.getSmallestSlice(), session, league);
        let gb = 0, fb = 0, ld = 0, pu = 0;
        const statsPerBallType = {};

        if (bbEvents.length === 0) {
            return statsPerBallType;
        }

        for (let bbe = 0; bbe < bbEvents.length; bbe++) {
            const bbeEvent = bbEvents[bbe];
            let ballType = this.resultsProcessor.getObjectContent(
                bbeEvent, ResultData.getNewDataPath(TaggingData.BattedBallType.key, DataSection.Tagging.key, ResultType.Pitch.key)
            );

            if (!ballType || ballType === BattedBallType.Unidentified.key) {
                ballType = this.resultsProcessor.getObjectContent(
                    bbeEvent, ResultData.getNewDataPath(TaggingData.BattedBallType.key, DataSection.Tagging.key, ResultType.Hit.key)
                );
            }

            if (!ballType || ballType === BattedBallType.Unidentified.key) {
                ballType = this.resultsProcessor.getObjectContent(
                    bbeEvent, ResultData.getNewDataPath(PitchReprocessed.BattedBallTypeR.key, DataSection.Tagging.key, ResultType.Pitch.key)
                );
            }

            if (!ballType || ballType === BattedBallType.Unidentified.key) {
                ballType = this.resultsProcessor.getObjectContent(
                    bbeEvent, ResultData.getNewDataPath(HitReprocessed.BattedBallTypeR.key, DataSection.Tagging.key, ResultType.Hit.key)
                );
            }

            switch (ballType) {
                case BattedBallType.FlyBall.key:
                    fb++;
                    break;

                case BattedBallType.LineDrive.key:
                    ld++;
                    break;

                case BattedBallType.PopUp.key:
                    pu++;
                    break;

                case BattedBallType.GroundBall.key:
                    gb++;
                    break;
            }
        }

        
        if (ld > 0) {
            const ldParam = this.supportedParams[0];
            statsPerBallType[ldParam.key] = RankingStat.fromParameter(
                ldParam,
                this.numberFormatter.format(ld),
                bbEvents.length
            );
        }

        const ldRateParam = this.supportedParams[4];
        statsPerBallType[ldRateParam.key] = RankingStat.fromParameter(
            ldRateParam,
            this.numberFormatter.format((ld * 100) / bbEvents.length),
            bbEvents.length
        );

        if (fb > 0) {
            const fbParam = this.supportedParams[1];
            statsPerBallType[fbParam.key] = RankingStat.fromParameter(
                fbParam,
                this.numberFormatter.format(fb),
                bbEvents.length
            );
        }

        const fbRateParam = this.supportedParams[5];
        statsPerBallType[fbRateParam.key] = RankingStat.fromParameter(
            fbRateParam,
            this.numberFormatter.format((fb * 100) / bbEvents.length),
            bbEvents.length
        );

        if (pu > 0) {
            const puParam = this.supportedParams[2];
            statsPerBallType[puParam.key] = RankingStat.fromParameter(
                puParam,
                this.numberFormatter.format(pu),
                bbEvents.length
            );
        }

        const puRateParam = this.supportedParams[6];
        statsPerBallType[puRateParam.key] = RankingStat.fromParameter(
            puRateParam,
            this.numberFormatter.format((pu * 100) / bbEvents.length),
            bbEvents.length
        );

        if (gb > 0) {
            const gbParam = this.supportedParams[3];
            statsPerBallType[gbParam.key] = RankingStat.fromParameter(
                gbParam,
                this.numberFormatter.format(gb),
                bbEvents.length
            );
        }

        const gbRateParam = this.supportedParams[7];
        statsPerBallType[gbRateParam.key] = RankingStat.fromParameter(
            gbRateParam,
            this.numberFormatter.format((gb * 100) / bbEvents.length),
            bbEvents.length
        );

        return statsPerBallType;
    }

    filterResults(results, session, league) {
        return this.contextualFilter.filter(results, session, league);
    }
}

export default BattedBallTypeRateCalculator;